import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { sendForm } from '../lib/sendEmail';

// ---------- Shared bits ----------

function Stepper({ step, labels }) {
  return (
    <div className="mb-10">
      <div className="flex items-center gap-2 mb-3">
        {labels.map((_, i) => {
          const n = i + 1;
          const state = n === step ? 'active' : n < step ? 'done' : 'idle';
          return (
            <React.Fragment key={n}>
              <div
                className={`w-7 h-7 rounded-full border flex items-center justify-center text-xs font-medium flex-shrink-0 transition-colors ${
                  state === 'active'
                    ? 'bg-gray-900 border-gray-900 text-white'
                    : state === 'done'
                    ? 'bg-white border-gray-900 text-gray-900'
                    : 'bg-white border-gray-300 text-gray-500'
                }`}
              >
                {n}
              </div>
              {n < labels.length && <div className="flex-1 h-px bg-gray-300" />}
            </React.Fragment>
          );
        })}
      </div>
      <div className="flex">
        {labels.map((label, i) => {
          const n = i + 1;
          const active = n === step;
          return (
            <div
              key={label}
              className={`flex-1 text-center text-[11px] uppercase tracking-widest ${
                active ? 'text-gray-900 font-medium' : 'text-gray-500'
              }`}
            >
              {label}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RadioCard({ name, value, checked, onChange, main, sub }) {
  return (
    <label
      className={`flex items-start gap-3 p-3 px-4 border cursor-pointer transition-colors ${
        checked
          ? 'border-gray-900 bg-[#f5f6ed]'
          : 'border-gray-300 hover:border-gray-500'
      }`}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        className="mt-1 accent-gray-900 w-4 h-4 flex-shrink-0"
      />
      <span className="flex-1">
        <span className="block text-sm font-medium text-gray-900 leading-snug">{main}</span>
        {sub && <span className="block text-xs text-gray-500 mt-1 leading-snug">{sub}</span>}
      </span>
    </label>
  );
}

function FieldLabel({ children, hint }) {
  return (
    <div className="mb-2">
      <label className="block text-sm font-medium text-gray-900">{children}</label>
      {hint && <p className="text-xs text-gray-500 mt-1 leading-snug">{hint}</p>}
    </div>
  );
}

function NavRow({ onBack, onNext, nextLabel = 'Continue', submitting = false }) {
  return (
    <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-300">
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-gray-600 hover:text-gray-900 bg-transparent border-0 p-0 cursor-pointer"
        >
          ← Back
        </button>
      ) : (
        <span />
      )}
      <button
        type="button"
        onClick={onNext}
        disabled={submitting}
        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? 'Sending…' : nextLabel}
      </button>
    </div>
  );
}

function wordCount(s) {
  return s.trim().split(/\s+/).filter(Boolean).length;
}

// ---------- Path picker ----------

function PathCard({ active, onSelect, eyebrow, title, description }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`text-left p-8 md:p-10 border transition-all duration-300 ${
        active ? 'border-gray-900 shadow-lg' : 'border-gray-200 hover:border-gray-400 hover:shadow-md'
      }`}
      style={{ backgroundColor: '#fbfcf3' }}
    >
      <span className="text-xs uppercase tracking-[0.3em] text-gray-500">{eyebrow}</span>
      <h3 className="text-2xl md:text-3xl font-light mt-3 mb-4">{title}</h3>
      <p className="text-gray-700 leading-relaxed">{description}</p>
      <div className="mt-6 inline-flex items-center text-sm uppercase tracking-widest text-gray-900">
        {active ? 'Selected' : 'Choose this path →'}
      </div>
    </button>
  );
}

// ---------- Success ----------

function SuccessState({ path, onReset }) {
  return (
    <div className="p-10 border border-gray-200" style={{ backgroundColor: '#fbfcf3' }}>
      <span className="text-xs uppercase tracking-[0.3em] text-gray-500">Application received</span>
      <h3 className="text-3xl font-light mt-4 mb-4">Thank you.</h3>
      <div className="w-16 h-px bg-gray-900 mb-6" />
      <p className="text-gray-700 leading-relaxed mb-4 max-w-xl">
        {path === 'user'
          ? "We review every application personally — regardless of why you applied. If you're selected, you'll hear from us within 5–7 days."
          : "We review professional applications with particular care. If selected, you'll receive a personal email within 5–7 days — along with context on how your feedback will be used."}
      </p>
      <p className="text-gray-700 leading-relaxed mb-8 max-w-xl">
        {path === 'user'
          ? 'Thank you for taking the time to be thoughtful about it.'
          : 'Your expertise matters to how this gets built.'}
      </p>
      <button type="button" onClick={onReset} className="btn-ghost">
        Submit another response
      </button>
    </div>
  );
}

// ---------- User wizard ----------

const USER_STEP_LABELS = ['Basics', 'Perspective', 'Motivation', 'Depth'];

function UserWizard({ onSubmitted }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [f, setF] = useState({
    name: '',
    email: '',
    age: '',
    role: '',
    mhview: '',
    perspective: '',
    draw: '',
    prior: '',
    priorDetail: '',
    why: '',
    value: '',
    commit: '',
    call: '',
    referral: '',
  });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setRadio = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const whyWords = wordCount(f.why);

  const submit = async () => {
    setSubmitting(true);
    setError('');
    const res = await sendForm({
      subject: `Bloora early access — user track — ${f.name || 'Applicant'}`,
      fields: {
        Track: 'User',
        Name: f.name,
        Email: f.email,
        'Age range': f.age,
        'Best describes you': f.role,
        'View on mental health': f.mhview,
        'Extra perspective': f.perspective,
        'What draws you here': f.draw,
        'Used wellness apps before': f.prior,
        'Prior tools detail': f.priorDetail,
        'Why early access': f.why,
        'What would make it valuable': f.value,
        'Willing to give 4-6 week feedback': f.commit,
        'Open to feedback call': f.call,
        'How they heard about Bloora': f.referral,
      },
    });
    setSubmitting(false);
    if (res.ok) onSubmitted();
    else setError(res.message);
  };

  return (
    <div className="p-8 md:p-10 border border-gray-200" style={{ backgroundColor: '#fbfcf3' }}>
      <div className="mb-8">
        <span className="text-xs uppercase tracking-[0.3em] text-gray-500">Early access — user track</span>
        <h2 className="text-2xl md:text-3xl font-light mt-3 mb-3">Apply, don't just sign up.</h2>
        <p className="text-gray-700 leading-relaxed">
          We're selecting a small group of people to help shape Bloora before launch. You don't need to be going through anything — we want curious minds, care-driven people, and everyday users equally.
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          {['Personal users', 'Social contributors', 'Curious explorers'].map((t) => (
            <span key={t} className="text-xs px-3 py-1 border border-gray-300 text-gray-700">{t}</span>
          ))}
        </div>
      </div>

      <Stepper step={step} labels={USER_STEP_LABELS} />

      {step === 1 && (
        <>
          <div className="mb-5">
            <FieldLabel>Full name</FieldLabel>
            <input type="text" className="input" placeholder="Your name" value={f.name} onChange={set('name')} />
          </div>
          <div className="mb-5">
            <FieldLabel>Email address</FieldLabel>
            <input type="email" className="input" placeholder="you@example.com" value={f.email} onChange={set('email')} />
          </div>
          <div className="mb-5">
            <FieldLabel hint="Optional — helps us understand the cohort">Age range</FieldLabel>
            <select className="input" value={f.age} onChange={set('age')}>
              <option value="">Select…</option>
              <option>Under 18</option>
              <option>18–24</option>
              <option>25–34</option>
              <option>35–44</option>
              <option>45–54</option>
              <option>55+</option>
            </select>
          </div>
          <div className="mb-2">
            <FieldLabel hint="Pick the one that feels closest — there's no wrong answer here">
              What best describes you?
            </FieldLabel>
            <div className="flex flex-col gap-2">
              <RadioCard name="role" value="personal" checked={f.role === 'personal'} onChange={setRadio('role')}
                main="Personal user" sub="I'm interested in using this for myself" />
              <RadioCard name="role" value="cause" checked={f.role === 'cause'} onChange={setRadio('role')}
                main="Social contributor" sub="I care about mental health as a cause and want to help build something meaningful" />
              <RadioCard name="role" value="curious" checked={f.role === 'curious'} onChange={setRadio('role')}
                main="Curious explorer" sub="I'm interested in the space and want to see where this goes" />
              <RadioCard name="role" value="multiple" checked={f.role === 'multiple'} onChange={setRadio('role')}
                main="A mix of the above" sub="More than one of these applies" />
            </div>
          </div>
          <NavRow onNext={() => setStep(2)} />
        </>
      )}

      {step === 2 && (
        <>
          <div className="bg-[#f5f6ed] border-l-2 border-gray-900 p-4 text-sm text-gray-700 leading-relaxed mb-6">
            No right answers here — we're trying to understand how you think about mental health, not where you are personally.
          </div>
          <div className="mb-5">
            <FieldLabel hint="Could be personal, philosophical, or both — whatever's honest">
              How do you think about mental health — in your own life or in general?
            </FieldLabel>
            <div className="flex flex-col gap-2">
              <RadioCard name="mhview" value="active" checked={f.mhview === 'active'} onChange={setRadio('mhview')}
                main="It's something I actively think about and work on" />
              <RadioCard name="mhview" value="aware" checked={f.mhview === 'aware'} onChange={setRadio('mhview')}
                main="I'm aware of it but don't have a strong routine" />
              <RadioCard name="mhview" value="social" checked={f.mhview === 'social'} onChange={setRadio('mhview')}
                main="I think about it more as a societal issue than a personal one" />
              <RadioCard name="mhview" value="curious2" checked={f.mhview === 'curious2'} onChange={setRadio('mhview')}
                main="I'm just curious — I don't have a strong prior view" />
            </div>
          </div>
          <div className="mb-2">
            <FieldLabel hint="Optional — but say it if you have an opinion">
              Anything else you'd add about how you see this space?
            </FieldLabel>
            <textarea
              rows="4"
              className="input resize-none"
              placeholder="e.g. I think most apps are too generic, or I'm skeptical of tech solving this, or I've seen what a good tool can do for someone close to me…"
              value={f.perspective}
              onChange={set('perspective')}
            />
          </div>
          <NavRow onBack={() => setStep(1)} onNext={() => setStep(3)} />
        </>
      )}

      {step === 3 && (
        <>
          <div className="mb-5">
            <FieldLabel hint="All three are equally valid reasons to apply">
              What draws you to this — personally, professionally, or as a cause?
            </FieldLabel>
            <div className="flex flex-col gap-2">
              <RadioCard name="draw" value="personal2" checked={f.draw === 'personal2'} onChange={setRadio('draw')}
                main="Personal experience" sub="I've navigated this myself or seen it up close" />
              <RadioCard name="draw" value="cause2" checked={f.draw === 'cause2'} onChange={setRadio('draw')}
                main="It's a cause I care about" sub="I want to contribute to something that matters beyond me" />
              <RadioCard name="draw" value="interest" checked={f.draw === 'interest'} onChange={setRadio('draw')}
                main="Genuine curiosity" sub="I find the problem interesting and want to be involved early" />
              <RadioCard name="draw" value="mixed2" checked={f.draw === 'mixed2'} onChange={setRadio('draw')}
                main="A combination" sub="Multiple things brought me here" />
            </div>
          </div>
          <div className="mb-5">
            <FieldLabel>Have you used any mental health or wellness apps before?</FieldLabel>
            <div className="grid grid-cols-2 gap-2">
              <RadioCard name="prior" value="yes" checked={f.prior === 'yes'} onChange={setRadio('prior')} main="Yes" />
              <RadioCard name="prior" value="no" checked={f.prior === 'no'} onChange={setRadio('prior')} main="No" />
            </div>
          </div>
          {f.prior === 'yes' && (
            <div className="mb-5">
              <FieldLabel>Which ones? What worked, what didn't?</FieldLabel>
              <textarea
                rows="3"
                className="input resize-none"
                placeholder="e.g. Tried Headspace — liked the structure, but it felt impersonal after a while…"
                value={f.priorDetail}
                onChange={set('priorDetail')}
              />
            </div>
          )}
          <div className="mb-2">
            <FieldLabel hint="Optional">Where did you hear about Bloora?</FieldLabel>
            <input
              type="text"
              className="input"
              placeholder="e.g. Twitter, a friend, our research, found it on Google…"
              value={f.referral}
              onChange={set('referral')}
            />
          </div>
          <NavRow onBack={() => setStep(2)} onNext={() => setStep(4)} />
        </>
      )}

      {step === 4 && (
        <>
          <div className="mb-5">
            <FieldLabel hint="Take a moment. This is the question we read most carefully.">
              Why do you want early access to this product?
            </FieldLabel>
            <textarea
              rows="5"
              className="input resize-none"
              placeholder="Not the polished version — the honest one. What actually brought you here?"
              value={f.why}
              onChange={set('why')}
            />
            <div className={`text-xs text-right mt-1 ${whyWords >= 50 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
              {whyWords} / 50 words minimum
            </div>
          </div>
          <div className="mb-5">
            <FieldLabel hint="Think about what's missing in the world right now, not just what you want from an app">
              What would make this product genuinely valuable — to you or to people like you?
            </FieldLabel>
            <textarea
              rows="4"
              className="input resize-none"
              placeholder="e.g. Something that treats people as complex rather than broken, or a tool that works for someone who doesn't think they 'need' help…"
              value={f.value}
              onChange={set('value')}
            />
          </div>
          <div className="mb-5">
            <FieldLabel>Are you willing to give honest, structured feedback over 4–6 weeks?</FieldLabel>
            <div className="flex flex-col gap-2">
              <RadioCard name="commit" value="yes" checked={f.commit === 'yes'} onChange={setRadio('commit')}
                main="Yes" sub="I'll show up and give real, useful feedback" />
              <RadioCard name="commit" value="maybe" checked={f.commit === 'maybe'} onChange={setRadio('commit')}
                main="Probably" sub="Depends on the time commitment" />
              <RadioCard name="commit" value="no" checked={f.commit === 'no'} onChange={setRadio('commit')}
                main="No — I just want to try it" />
            </div>
          </div>
          <div className="mb-2">
            <FieldLabel>Open to a short feedback call if selected?</FieldLabel>
            <div className="grid grid-cols-2 gap-2">
              <RadioCard name="call" value="yes" checked={f.call === 'yes'} onChange={setRadio('call')} main="Yes" />
              <RadioCard name="call" value="no" checked={f.call === 'no'} onChange={setRadio('call')} main="No" />
            </div>
          </div>
          {error && (
            <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded">{error}</p>
          )}
          <NavRow
            onBack={() => setStep(3)}
            onNext={submit}
            nextLabel="Submit application"
            submitting={submitting}
          />
        </>
      )}
    </div>
  );
}

// ---------- Professional wizard ----------

const PRO_STEP_LABELS = ['Identity', 'Practice', 'Gaps', 'Fit'];

function ProWizard({ onSubmitted }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [f, setF] = useState({
    fname: '',
    lname: '',
    email: '',
    role: '',
    license: '',
    spec: '',
    clients: '',
    years: '',
    setting: '',
    modalities: '',
    languages: '',
    track: '',
    missing: '',
    concern: '',
    why: '',
    commit: '',
    call: '',
  });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setRadio = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const whyWords = wordCount(f.why);

  const submit = async () => {
    setSubmitting(true);
    setError('');
    const res = await sendForm({
      subject: `Bloora early access — professional track — ${f.fname} ${f.lname}`.trim(),
      fields: {
        Track: 'Professional',
        'First name': f.fname,
        'Last name': f.lname,
        Email: f.email,
        Role: f.role,
        'License / credential (optional)': f.license,
        Specialisation: f.spec,
        'Clients seen weekly': f.clients,
        'Years in practice': f.years,
        'Practice setting': f.setting,
        'Therapeutic modalities': f.modalities,
        'Languages spoken': f.languages,
        'How they currently track client progress': f.track,
        "What's missing in existing tools": f.missing,
        'Biggest concern about AI in the field': f.concern,
        'Why they want to be part of this': f.why,
        'Willing to give 4-8 week feedback': f.commit,
        'Open to 20-min onboarding call': f.call,
      },
    });
    setSubmitting(false);
    if (res.ok) onSubmitted();
    else setError(res.message);
  };

  return (
    <div className="p-8 md:p-10 border border-gray-200" style={{ backgroundColor: '#fbfcf3' }}>
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs uppercase tracking-[0.3em] text-gray-500">Early access</span>
          <span className="text-xs px-3 py-1 border border-gray-900 text-gray-900">Professional track</span>
        </div>
        <h2 className="text-2xl md:text-3xl font-light mb-3">We want to build the tool you actually need.</h2>
        <p className="text-gray-700 leading-relaxed">
          This track is for psychologists, therapists, and clinicians. We're selecting a small number of practitioners to co-shape Bloora from day one.
        </p>
      </div>

      <Stepper step={step} labels={PRO_STEP_LABELS} />

      {step === 1 && (
        <>
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div>
              <FieldLabel>First name</FieldLabel>
              <input type="text" className="input" placeholder="First" value={f.fname} onChange={set('fname')} />
            </div>
            <div>
              <FieldLabel>Last name</FieldLabel>
              <input type="text" className="input" placeholder="Last" value={f.lname} onChange={set('lname')} />
            </div>
          </div>
          <div className="mb-5">
            <FieldLabel>Professional email</FieldLabel>
            <input type="email" className="input" placeholder="you@practice.com" value={f.email} onChange={set('email')} />
          </div>
          <div className="mb-5">
            <FieldLabel hint="Optional — helps us verify and prioritize">License / credential number</FieldLabel>
            <input type="text" className="input" placeholder="e.g. RCI #A12345, BACP MBACP, APA member ID…" value={f.license} onChange={set('license')} />
          </div>
          <div className="mb-2">
            <FieldLabel>Your role</FieldLabel>
            <div className="flex flex-col gap-2">
              <RadioCard name="role" value="psychologist" checked={f.role === 'psychologist'} onChange={setRadio('role')} main="Psychologist" />
              <RadioCard name="role" value="therapist" checked={f.role === 'therapist'} onChange={setRadio('role')} main="Therapist / counsellor" />
              <RadioCard name="role" value="psychiatrist" checked={f.role === 'psychiatrist'} onChange={setRadio('role')} main="Psychiatrist" />
              <RadioCard name="role" value="researcher" checked={f.role === 'researcher'} onChange={setRadio('role')} main="Researcher / academic" />
              <RadioCard name="role" value="other" checked={f.role === 'other'} onChange={setRadio('role')} main="Other clinical role" />
            </div>
          </div>
          <NavRow onNext={() => setStep(2)} />
        </>
      )}

      {step === 2 && (
        <>
          <div className="mb-5">
            <FieldLabel>Area of specialisation</FieldLabel>
            <input type="text" className="input" placeholder="e.g. anxiety, trauma, adolescents, couples…" value={f.spec} onChange={set('spec')} />
          </div>
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div>
              <FieldLabel>Clients seen weekly</FieldLabel>
              <input type="number" min="1" max="100" className="input" placeholder="e.g. 12" value={f.clients} onChange={set('clients')} />
            </div>
            <div>
              <FieldLabel>Years in practice</FieldLabel>
              <input type="number" min="0" max="50" className="input" placeholder="e.g. 7" value={f.years} onChange={set('years')} />
            </div>
          </div>
          <div className="mb-5">
            <FieldLabel>Practice setting</FieldLabel>
            <div className="flex flex-col gap-2">
              <RadioCard name="setting" value="private" checked={f.setting === 'private'} onChange={setRadio('setting')} main="Private practice" />
              <RadioCard name="setting" value="clinic" checked={f.setting === 'clinic'} onChange={setRadio('setting')} main="Clinic or hospital" />
              <RadioCard name="setting" value="nonprofit" checked={f.setting === 'nonprofit'} onChange={setRadio('setting')} main="Non-profit or community" />
              <RadioCard name="setting" value="academic" checked={f.setting === 'academic'} onChange={setRadio('setting')} main="Academic or research" />
            </div>
          </div>
          <div className="mb-5">
            <FieldLabel hint="Optional — comma separated">Therapeutic modalities</FieldLabel>
            <input type="text" className="input" placeholder="e.g. CBT, DBT, EMDR, psychodynamic, ACT…" value={f.modalities} onChange={set('modalities')} />
          </div>
          <div className="mb-2">
            <FieldLabel hint="Optional — helps us understand client reach">Languages you practice in</FieldLabel>
            <input type="text" className="input" placeholder="e.g. English, Hindi, Urdu…" value={f.languages} onChange={set('languages')} />
          </div>
          <NavRow onBack={() => setStep(1)} onNext={() => setStep(3)} />
        </>
      )}

      {step === 3 && (
        <>
          <div className="bg-[#f5f6ed] border-l-2 border-gray-900 p-4 text-sm text-gray-700 leading-relaxed mb-6">
            These questions matter most. Your answers directly shape what we build.
          </div>
          <div className="mb-5">
            <FieldLabel hint="Tools, methods, apps, or nothing — be specific">
              How do you currently track or support client progress between sessions?
            </FieldLabel>
            <textarea
              rows="4"
              className="input resize-none"
              placeholder="e.g. I rely on session notes and client self-report — there's no real-time visibility between appointments…"
              value={f.track}
              onChange={set('track')}
            />
          </div>
          <div className="mb-5">
            <FieldLabel hint="What do you wish existed but doesn't?">What's missing in existing tools?</FieldLabel>
            <textarea
              rows="4"
              className="input resize-none"
              placeholder="e.g. Something that surfaces patterns without requiring clients to manually log everything…"
              value={f.missing}
              onChange={set('missing')}
            />
          </div>
          <div className="mb-2">
            <FieldLabel hint="We want honest scepticism, not enthusiasm">
              What's your biggest concern about AI in your field?
            </FieldLabel>
            <textarea
              rows="4"
              className="input resize-none"
              placeholder="e.g. Over-reliance, misinterpretation of clinical signals, erosion of the therapeutic relationship…"
              value={f.concern}
              onChange={set('concern')}
            />
          </div>
          <NavRow onBack={() => setStep(2)} onNext={() => setStep(4)} />
        </>
      )}

      {step === 4 && (
        <>
          <div className="mb-5">
            <FieldLabel hint="What's the professional or personal reason — not the polished version">
              Why do you want to be part of this?
            </FieldLabel>
            <textarea
              rows="5"
              className="input resize-none"
              placeholder="Take a moment with this one…"
              value={f.why}
              onChange={set('why')}
            />
            <div className={`text-xs text-right mt-1 ${whyWords >= 50 ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
              {whyWords} / 50 words minimum
            </div>
          </div>
          <div className="mb-5">
            <FieldLabel>Are you willing to give structured clinical feedback over 4–8 weeks?</FieldLabel>
            <div className="flex flex-col gap-2">
              <RadioCard name="commit" value="yes" checked={f.commit === 'yes'} onChange={setRadio('commit')}
                main="Yes — I can commit to structured feedback" />
              <RadioCard name="commit" value="limited" checked={f.commit === 'limited'} onChange={setRadio('commit')}
                main="Partially — limited time, but willing" />
              <RadioCard name="commit" value="no" checked={f.commit === 'no'} onChange={setRadio('commit')}
                main="No" />
            </div>
          </div>
          <div className="mb-2">
            <FieldLabel>Open to a 20-minute onboarding call?</FieldLabel>
            <div className="grid grid-cols-2 gap-2">
              <RadioCard name="call" value="yes" checked={f.call === 'yes'} onChange={setRadio('call')} main="Yes" />
              <RadioCard name="call" value="no" checked={f.call === 'no'} onChange={setRadio('call')} main="No" />
            </div>
          </div>
          {error && (
            <p className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded">{error}</p>
          )}
          <NavRow
            onBack={() => setStep(3)}
            onNext={submit}
            nextLabel="Submit application"
            submitting={submitting}
          />
        </>
      )}
    </div>
  );
}

// ---------- Page ----------

export default function EarlyAccess() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTrack = useMemo(() => {
    const t = searchParams.get('track');
    if (t === 'user' || t === 'pro') return t;
    return null;
  }, [searchParams]);

  const [path, setPath] = useState(initialTrack);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (initialTrack && initialTrack !== path) setPath(initialTrack);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTrack]);

  const choosePath = (p) => {
    setPath(p);
    setSearchParams({ track: p });
    // Scroll the wizard into view so the user doesn't lose context
    setTimeout(() => {
      const el = document.getElementById('ea-wizard');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const reset = () => {
    setDone(false);
    setPath(null);
    setSearchParams({});
  };

  return (
    <>
      {/* Hero */}
      <section className="px-6 pt-12 pb-16 md:pt-20 md:pb-20 background-grid">
        <div className="max-w-5xl mx-auto">
          <span className="text-xs uppercase tracking-[0.3em] text-gray-500">Early Access</span>
          <h1 className="text-4xl md:text-6xl font-light mt-4 mb-6 leading-tight">
            Be among the first to use Bloora.
          </h1>
          <div className="w-24 h-px bg-gray-900 mb-8" />
          <p className="text-lg text-gray-700 leading-relaxed max-w-3xl">
            Bloora is currently in pre-launch. We're inviting two groups to shape the product before it ships: individuals who want a smarter way to understand themselves, and clinicians or researchers who can ground the system in real practice.
          </p>
        </div>
      </section>

      {/* Path selector + wizard */}
      <section id="ea-wizard" className="px-6 pb-32" style={{ backgroundColor: '#f5f6ed' }}>
        <div className="max-w-5xl mx-auto pt-16">
          {done ? (
            <SuccessState path={path} onReset={reset} />
          ) : (
            <>
              <div className="grid md:grid-cols-2 gap-6 mb-12">
                <PathCard
                  active={path === 'user'}
                  onSelect={() => choosePath('user')}
                  eyebrow="For Individuals"
                  title="I'm a user"
                  description="You want a thoughtful, data-driven way to understand yourself. We'll invite you to the next user cohort when Bloora opens."
                />
                <PathCard
                  active={path === 'pro'}
                  onSelect={() => choosePath('pro')}
                  eyebrow="For Professionals"
                  title="I'm a clinician or researcher"
                  description="You're a therapist, psychiatrist, or researcher. You'll get access to the clinician dashboard and a direct line to shape the product."
                />
              </div>

              {path === 'user' && <UserWizard onSubmitted={() => setDone(true)} />}
              {path === 'pro' && <ProWizard onSubmitted={() => setDone(true)} />}

              <p className="mt-8 text-sm text-gray-500">
                By submitting, you agree to our{' '}
                <Link to="/privacy" className="underline">Privacy Policy</Link> and{' '}
                <Link to="/terms" className="underline">Terms of Use</Link>.
              </p>
            </>
          )}
        </div>
      </section>
    </>
  );
}
